// Market movement charts — types + shaping (Phase 3).
//
// Each movement widget (Match Goals/Cards - Movement, Winner (1x2) - Movement,
// and later the Player movements) shows how one market line's odds drifted over
// time: a stat header (OPEN / NOW / MOVE, coloured by direction) + a D3 line
// chart. The data + labels differ per widget; the shaping (bookmaker offset,
// open/now/move, direction colours) is shared and pure. The component owns the
// D3 chart (rebuilt each draw, no animation).

/** One point on a movement series: a timestamp label + decimal odds. */
export interface MovementPoint {
  t: string;
  o: number;
}
export type MovementSeries = MovementPoint[];

/** Goals/Cards movement: fixture → scope → line key → series. */
export type GoalsCardsMovementData = Record<
  string,
  Record<string, Record<string, MovementSeries>>
>;
/** Winner (1x2) movement: fixture → outcome key → series (no scope). */
export type WinnerMovementData = Record<string, Record<string, MovementSeries>>;
/**
 * Player goals/cards movement: fixture → market type ("Anytime" | "First" | "2+")
 * → player → series. Note the middle key is the **market type**, NOT the scope —
 * scope only filters which players are shown (see `resolvePlayersInScope`).
 */
export type PlayerMovementData = Record<
  string,
  Record<string, Record<string, MovementSeries>>
>;
/** Line key → display label (e.g. "over_1.5" → "O/1.5"). */
export type MovementLabels = Record<string, string>;
/** Bookmaker → a flat odds offset applied to the base (Pinnacle) series. */
export type BkOffsets = Record<string, number>;

/** Shaped movement widget: the series + the stat-header values/colours. */
export interface MovementShape {
  /** The offset-adjusted series (what the chart plots). */
  pts: MovementPoint[];
  label: string;
  /** Opening odds, `toFixed(2)`. */
  openStr: string;
  /** Current odds, `toFixed(2)`. */
  nowStr: string;
  /** Move, e.g. "▲ +0.12" / "▼ -0.08" / "–". */
  moveStr: string;
  /** Arrow colour: grey flat, green shortened (move<0), red drifted. */
  arrowColour: string;
  /** Move-value colour: opposite of the arrow (red shortened, green drifted). */
  moveColour: string;
}

/**
 * Shape a movement series: apply the bookmaker offset (rounded to 2dp), then
 * derive the open/now/move stat header. Colours match the legacy: a shortened
 * line (move < 0) is good — green arrow, red move value; a drifted line is the
 * reverse. Returns null for an empty series.
 */
export function shapeMovement(
  base: MovementSeries,
  offset: number,
  label: string
): MovementShape | null {
  if (!base || !base.length) return null;
  const pts = base.map((d) => ({
    t: d.t,
    o: Math.round((d.o + offset) * 100) / 100,
  }));
  const open = pts[0]!.o;
  const now = pts[pts.length - 1]!.o;
  const move = Math.round((now - open) * 100) / 100;
  const shortened = move < 0;
  const arrowColour = move === 0 ? "#888" : shortened ? "#2ecc71" : "#e74c3c";
  const moveColour = move === 0 ? "#888" : shortened ? "#e74c3c" : "#2ecc71";
  const tri = move < 0 ? "▼" : move > 0 ? "▲" : "–";
  const moveStr =
    move === 0 ? "–" : tri + " " + (move > 0 ? "+" : "") + move.toFixed(2);
  return {
    pts,
    label,
    openStr: open.toFixed(2),
    nowStr: now.toFixed(2),
    moveStr,
    arrowColour,
    moveColour,
  };
}

// ── Player movement helpers ─────────────────────────────────────────────────
// The player movement widgets (Player Goals/Cards - Movement) show one player's
// odds drift. The player LIST is filtered by the market scope (a team abbr or
// "Match"); the SELECTED player + label are resolved from that list. These are
// pure so the component can own the list/selection (it populates the shell
// .player-toggle itself, since on first mount the toggle is empty).

/**
 * The players to offer for a movement scope, in the underlying data's order.
 * `scopeNames` is the set of player names present in the paired static's rows
 * for the current scope+market type; pass `null` for the "Match" scope (all
 * players). Mirrors the legacy: filter to the scope's players, but fall back to
 * all players if that filter is empty (so an unknown scope never blanks out).
 */
export function resolvePlayersInScope(
  allPlayers: string[],
  scopeNames: string[] | null
): string[] {
  if (!scopeNames) return allPlayers;
  const set = new Set(scopeNames);
  const filtered = allPlayers.filter((p) => set.has(p));
  return filtered.length ? filtered : allPlayers;
}

/**
 * The selected player: keep the previous selection (the toggle's current value)
 * if it is still in scope, else default to the first. Mirrors the legacy
 * "keep prev if valid else first" toggle behaviour. `""` if the list is empty.
 */
export function resolveSelectedPlayer(
  players: string[],
  prev: string | null | undefined
): string {
  if (prev && players.includes(prev)) return prev;
  return players[0] ?? "";
}

/**
 * The stat-header label for a player movement, matching the legacy renderers:
 * goals → "<player> — 2+ Goals" / "<player> — <type> Scorer";
 * cards → "<player> — First Booked" / "<player> — Anytime Booked".
 * The dash is an em-dash (U+2014), matching the legacy source exactly.
 */
export function playerMovementLabel(
  player: string,
  marketType: string,
  kind: "goals" | "cards"
): string {
  if (kind === "cards") {
    return player + " — " + (marketType === "First" ? "First Booked" : "Anytime Booked");
  }
  return player + " — " + (marketType === "2+" ? "2+ Goals" : marketType + " Scorer");
}
